// ABOUTME: Job detail overview of one attempt: the result sections the job's presentation declares,
// ABOUTME: followed by the attempt logs. Unknown types get a generic set of sections.

import { Alert, Badge, Box, IconButton, Stack, Typography, WarningIcon } from '@ds/coolshop';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { ReactNode, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getJobPresentation, JobPresentationContext } from '../../../registry';
import { DetailType, JobResultView } from '../../../types';
import { getJobCounters, getJobData, getJobLogs } from '../JobData.helpers';
import { JobOverviewSection } from './sections/JobOverviewSection';

interface IDetailOverviewProps {
    job: JobResultView;
    handleToggleDetails: (title: DetailType) => void;
    /** Last, taking the remaining height (the attempt logs). */
    afterSections?: ReactNode;
}

export function JobOverview(props: Readonly<IDetailOverviewProps>) {
    const { job, handleToggleDetails, afterSections } = props;
    const intl = useIntl();
    const { cgtSettings } = useContext(SettingsContext);

    const priceListHierarchyId = cgtSettings?.hierarchies?.priceListHierarchyId ?? undefined;
    const presentation = getJobPresentation(job.type);
    const { warnings } = getJobLogs(job);
    // Recorded only by the jobs that send a notification email; recipients are deliberately
    // not exposed here.
    const { notificationEmailSent } = (job.data ?? {}) as { notificationEmailSent?: boolean };

    const ctx = useMemo<JobPresentationContext>(
        () => ({
            job,
            counters: getJobCounters(job, priceListHierarchyId),
            priceListHierarchyId,
            aggregateId: getJobData(job).aggregateId,
        }),
        [job, priceListHierarchyId],
    );

    return (
        <Stack gap={2.5} flex={1} minHeight={0}>
            {/* Centred on the "Job" section header of the side column, so both titles line up. */}
            <Box display="flex" alignItems="center" justifyContent="space-between" height={44}>
                <Typography color="primary" variant="h6">
                    <b>
                        {intl.formatMessage({
                            id: 'job.overview.results',
                            defaultMessage: 'Results',
                        })}
                    </b>
                </Typography>
                {warnings.length > 0 && (
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({
                            id: 'job.overview.warnings',
                            defaultMessage: 'Warnings',
                        })}
                        onClick={() => handleToggleDetails(DetailType.WARNINGS)}
                    >
                        <Badge badgeContent={warnings.length} color="warning">
                            <WarningIcon />
                        </Badge>
                    </IconButton>
                )}
            </Box>
            {presentation.description && (
                <Typography color="text.secondary">
                    {intl.formatMessage(presentation.description)}
                </Typography>
            )}
            {/* Only the jobs that attempt a notification email record the outcome. */}
            {notificationEmailSent !== undefined && (
                <Alert severity={notificationEmailSent ? 'success' : 'error'}>
                    {notificationEmailSent
                        ? intl.formatMessage({
                              id: 'job.notificationEmail.sent',
                              defaultMessage: 'The notification email was sent.',
                          })
                        : intl.formatMessage({
                              id: 'job.notificationEmail.failed',
                              defaultMessage: 'The notification email was not sent.',
                          })}
                </Alert>
            )}
            {presentation.sections.map(section => (
                <JobOverviewSection
                    key={section}
                    section={section}
                    presentation={presentation}
                    ctx={ctx}
                    handleToggleDetails={handleToggleDetails}
                />
            ))}
            {afterSections}
        </Stack>
    );
}
